import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { poaAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { isPoaOnlyUser } from '@/lib/plan-access';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Download, Shield, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function PoaPage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await poaAPI.getTemplates();
        setTemplates(res.data || {});
      } catch (err) {
        console.error(err);
        toast.error('Could not load POA templates');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleDownload = (key, template) => {
    const content = `${template.title}\n${'='.repeat(60)}\n\nDate: ${new Date().toLocaleDateString()}\n\n--- ROOT CAUSE ANALYSIS ---\n${template.root_cause}\n\n--- CORRECTIVE ACTIONS TAKEN ---\n${template.corrective_actions}\n\n--- PREVENTIVE MEASURES ---\n${template.preventive_measures}\n\n--- SIGNATURE ---\n[Your Name]\n[Account Name]\n[Date]`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `POA-${key.replace(/[^a-zA-Z0-9]/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('POA template downloaded');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-40" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="poa-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold">Plan of Action (POA) Templates</h1>
          <p className="text-sm text-muted-foreground">
            Download ready-to-submit POA templates for Amazon policy violations
          </p>
        </div>
        <Button asChild className="shrink-0">
          <Link to="/poa-wizard">
            Open POA step form
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>

      {!isPoaOnlyUser(user) && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-muted-foreground">
          For a customized reinstatement intake (connect Seller Central + notices + 5-part form), use{' '}
          <Link to="/poa-wizard" className="font-medium text-primary hover:underline">
            POA step form
          </Link>
          .
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {Object.entries(templates).map(([key, template]) => (
          <Card key={key} className="border-border/80 bg-card transition-colors hover:border-border">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-[hsl(188,100%,55%)]" />
                  <CardTitle className="text-sm font-semibold">{template.title}</CardTitle>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDownload(key, template)}
                  className="h-8 gap-1.5 px-3 text-xs"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Root Cause
                  </p>
                  <p className="whitespace-pre-line text-xs text-foreground/80">
                    {expanded === key
                      ? template.root_cause
                      : `${template.root_cause?.slice(0, 200) || ''}...`}
                  </p>
                </div>
                {expanded === key && (
                  <>
                    <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Corrective Actions
                      </p>
                      <p className="whitespace-pre-line text-xs text-foreground/80">
                        {template.corrective_actions}
                      </p>
                    </div>
                    <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Preventive Measures
                      </p>
                      <p className="whitespace-pre-line text-xs text-foreground/80">
                        {template.preventive_measures}
                      </p>
                    </div>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === key ? null : key)}
                  className="text-xs text-[hsl(188,100%,55%)] hover:underline"
                >
                  {expanded === key ? 'Show Less' : 'View Full Template'}
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
